import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { companyApiService } from '../services/companyApi';

interface CompanyProfile {
  name: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  instagram_url?: string;
}

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CompanyProfile>({
    name: 'IRIS SOFTECH',
    email: 'softechiris@gmail.com',
    phone: '771742350',
    address: 'Medina RUE 27 X 22 ENSUITE'
  });

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const data = await companyApiService.getCompanyProfilePublic();
        setProfile({
          name: data.name || 'IRIS SOFTECH',
          email: 'softechiris@gmail.com',
          phone: '771742350',
          address: 'Medina RUE 27 X 22 ENSUITE',
          logo_url: data.logo_url,
          facebook_url: data.facebook_url,
          twitter_url: data.twitter_url,
          linkedin_url: data.linkedin_url,
          instagram_url: data.instagram_url
        });
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
      }
    };
    
    loadCompanyProfile();
  }, []);

  return (
    <footer className="bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* À propos */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src={profile.logo_url || "/images/irislogobgremoved.png"} 
                alt={`${profile.name} Logo`} 
                className="w-12 h-12 object-contain"
              />
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                {profile.name}
              </h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Solutions Digitales Innovantes pour transformer vos idées en réalité.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-400">Navigation</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => navigate('/')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  Accueil
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/services')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/packs')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  Packs
                </button>
              </li>
              <li>
                <button onClick={() => window.alert('Bientôt disponible')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  Intelligence Artificielle
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/aboutirissoftech')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  À propos
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/contact')} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-400">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Mail className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" />
                <a href={`mailto:${profile.email}`} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  {profile.email}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Phone className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" />
                <a href={`tel:${profile.phone.replace(/\s/g, '')}`} className="text-gray-300 hover:text-amber-400 transition-colors text-sm">
                  {profile.phone}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" />
                <span className="text-gray-300 text-sm">{profile.address}</span>
              </li>
            </ul>
          </div>

          {/* Réseaux sociaux */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-400">Suivez-nous</h4>
            <div className="flex space-x-4">
              {profile.facebook_url && (
                <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber-400 transition-all duration-300">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {profile.twitter_url && (
                <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber-400 transition-all duration-300">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {profile.linkedin_url && (
                <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber-400 transition-all duration-300">
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {profile.instagram_url && (
                <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-amber-400 transition-all duration-300">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
            <button
              onClick={() => navigate('/contact')}
              className="mt-6 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300"
            >
              Nous contacter
            </button>
          </div>
        </div>

        {/* Ligne de séparation */}
        <div className="border-t border-white/10 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© {new Date().getFullYear()} {profile.name}. Tous droits réservés.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button className="hover:text-amber-400 transition-colors">
                Mentions légales
              </button>
              <button className="hover:text-amber-400 transition-colors">
                Politique de confidentialité
              </button>
              <button className="hover:text-amber-400 transition-colors">
                CGV
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
